import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';
import { contextService, type Context as TrackedContext } from '@services/contextService';
import {
  remediationService,
  type RemediationItem,
  type RemediationItemStatus,
  type RemediationPlanDetail,
  type RemediationPriority,
} from '@services/remediationService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';

const PRIORITY_ORDER: RemediationPriority[] = ['critical', 'high', 'normal', 'low'];
const STATUSES: RemediationItemStatus[] = ['open', 'in_progress', 'done', 'wont_fix'];

const PRIORITY_CHIP: Record<RemediationPriority, 'error' | 'warning' | 'default' | 'info'> = {
  critical: 'error',
  high: 'warning',
  normal: 'default',
  low: 'info',
};

interface ItemFormState {
  id?: string;
  title: string;
  description: string;
  priority: RemediationPriority;
  status: RemediationItemStatus;
  context_id: string;
}

const emptyForm: ItemFormState = {
  title: '',
  description: '',
  priority: 'normal',
  status: 'open',
  context_id: '',
};

/**
 * REV-214: detail view for a single RemediationPlan. Shows items grouped by
 * priority band, supports add/edit/delete for items, and surfaces each
 * item's Context so the cross-context span of the plan is visible at a
 * glance.
 */
export default function RemediationPlanDetailPage() {
  const { t } = useTranslation();
  const { slug, planId } = useParams<{ slug: string; planId: string }>();

  const [orgId, setOrgId] = useState<string | null>(null);
  const [plan, setPlan] = useState<RemediationPlanDetail | null>(null);
  const [contexts, setContexts] = useState<TrackedContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ItemFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug || !planId) return;
    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
      const [p, ctxs] = await Promise.all([
        remediationService.getPlan(org.id, planId),
        contextService.list(org.id, { limit: 100 }),
      ]);
      setPlan(p);
      setContexts(ctxs.items ?? []);
    } catch (err) {
      setError(toDisplayError(err, t('remediation.errors.planFetchFailed', 'Failed to load plan')));
    } finally {
      setLoading(false);
    }
  }, [slug, planId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const groupedItems = useMemo(() => {
    if (!plan) return new Map<RemediationPriority, RemediationItem[]>();
    const map = new Map<RemediationPriority, RemediationItem[]>();
    PRIORITY_ORDER.forEach((p) => map.set(p, []));
    plan.items.forEach((i) => {
      const bucket = map.get(i.priority);
      if (bucket) bucket.push(i);
    });
    return map;
  }, [plan]);

  const openCreate = () => {
    setForm(emptyForm);
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (item: RemediationItem) => {
    setForm({
      id: item.id,
      title: item.title,
      description: item.description || '',
      priority: item.priority,
      status: item.status,
      context_id: item.context_id || '',
    });
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!orgId || !plan) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        status: form.status,
        context_id: form.context_id || null,
      };
      if (form.id) {
        await remediationService.updateItem(orgId, plan.id, form.id, payload);
      } else {
        await remediationService.createItem(orgId, plan.id, payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setSaveError(
        toDisplayError(err, t('remediation.errors.itemSaveFailed', 'Failed to save item'))
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: RemediationItem) => {
    if (!orgId || !plan) return;
    // Keep the confirm lightweight — author-or-manager gate is server-side.
    if (!window.confirm(t('remediation.confirmDelete', 'Delete this item?'))) return;
    try {
      await remediationService.removeItem(orgId, plan.id, item.id);
      await load();
    } catch (err) {
      setError(
        toDisplayError(err, t('remediation.errors.itemDeleteFailed', 'Failed to delete item'))
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to={`/orgs/${slug}/remediation`}
        >
          {t('remediation.backToPlans', 'Back to plans')}
        </Button>
      </Box>
    );
  }

  if (!plan) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to={`/orgs/${slug}/remediation`}
        sx={{ mb: 2 }}
      >
        {t('remediation.backToPlans', 'Back to plans')}
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {plan.name}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {t('remediation.addItem', 'Add item')}
        </Button>
      </Box>
      {plan.description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {plan.description}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {PRIORITY_ORDER.map((priority) => {
        const items = groupedItems.get(priority) || [];
        if (items.length === 0) return null;
        return (
          <Paper variant="outlined" sx={{ mb: 3 }} key={priority}>
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                color={PRIORITY_CHIP[priority]}
                label={t(`remediation.priorities.${priority}`, priority)}
              />
              <Typography variant="subtitle2">
                {t('remediation.itemCount', {
                  count: items.length,
                  defaultValue: '{{count}} items',
                })}
              </Typography>
            </Box>
            <Divider />
            <ScrollableTableContainer>
              <Table
                size="small"
                aria-label={t('remediation.priorityTableLabel', {
                  defaultValue: '{{priority}} priority items',
                  priority,
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('remediation.columns.itemTitle', 'Title')}</TableCell>
                    <TableCell>{t('remediation.columns.context', 'Context')}</TableCell>
                    <TableCell>{t('remediation.columns.status', 'Status')}</TableCell>
                    <TableCell align="right">
                      {t('remediation.columns.actions', 'Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.title}
                        </Typography>
                        {item.description && (
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        )}
                        {item.finding_instance_id && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              size="small"
                              variant="outlined"
                              color="info"
                              // REV-732: an anchor, because it goes to a URL.
                              //
                              // `clickable` was added to make MUI render a
                              // focusable, keyboard-operable control instead of
                              // a plain div — the right instinct, but it stops
                              // at a BUTTON. As a link it also gets cmd-click,
                              // middle-click, "open in new tab" and a preview of
                              // the destination, and is announced as a link.
                              // MUI keeps the clickable styling for component="a".
                              clickable
                              component={RouterLink}
                              to={`/orgs/${slug}/findings/${item.finding_instance_id}`}
                              label={t('remediation.linkedFinding', 'Linked finding')}
                              aria-label={t('remediation.viewLinkedFinding', 'View linked finding')}
                            />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.context
                          ? item.context.name
                          : t('remediation.allContexts', 'Cross-context')}
                      </TableCell>
                      <TableCell>
                        {t(`remediation.itemStatuses.${item.status}`, item.status)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={t('remediation.editItem', 'Edit item')}
                          onClick={() => openEdit(item)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={t('remediation.deleteItem', 'Delete item')}
                          onClick={() => handleDelete(item)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          </Paper>
        );
      })}

      {plan.items.length === 0 && (
        <Alert severity="info">
          {t(
            'remediation.emptyItems',
            'No remediation items yet. Add your first item to start the roadmap.'
          )}
        </Alert>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {form.id
            ? t('remediation.itemDialog.editTitle', 'Edit item')
            : t('remediation.itemDialog.createTitle', 'Add item')}
        </DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('remediation.itemDialog.title', 'Title')}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              // autoFocus is correct for a modal dialog: the user just
              // opened the dialog intending to type here.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              inputProps={{ maxLength: 500 }}
              fullWidth
            />
            <TextField
              label={t('remediation.itemDialog.description', 'Description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="remediation-priority-label">
                {t('remediation.itemDialog.priority', 'Priority')}
              </InputLabel>
              <Select
                labelId="remediation-priority-label"
                id="remediation-priority"
                value={form.priority}
                label={t('remediation.itemDialog.priority', 'Priority')}
                onChange={(e) =>
                  setForm({ ...form, priority: e.target.value as RemediationPriority })
                }
              >
                {PRIORITY_ORDER.map((p) => (
                  <MenuItem key={p} value={p}>
                    {t(`remediation.priorities.${p}`, p)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="remediation-status-label">
                {t('remediation.itemDialog.status', 'Status')}
              </InputLabel>
              <Select
                labelId="remediation-status-label"
                id="remediation-status"
                value={form.status}
                label={t('remediation.itemDialog.status', 'Status')}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as RemediationItemStatus })
                }
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`remediation.itemStatuses.${s}`, s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="remediation-context-label">
                {t('remediation.itemDialog.context', 'Context (optional)')}
              </InputLabel>
              <Select
                labelId="remediation-context-label"
                id="remediation-context"
                value={form.context_id}
                label={t('remediation.itemDialog.context', 'Context (optional)')}
                onChange={(e) => setForm({ ...form, context_id: e.target.value })}
              >
                <MenuItem value="">
                  <em>{t('remediation.allContexts', 'Cross-context')}</em>
                </MenuItem>
                {contexts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || form.title.trim().length === 0}
          >
            {saving
              ? t('common.saving', 'Saving…')
              : form.id
                ? t('common.save', 'Save')
                : t('remediation.itemDialog.create', 'Add item')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
